import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockTextField from '../components/GuideMockTextField';
import GuideMockToggleButtonGroup from '../components/GuideMockToggleButtonGroup';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideNoticeCreateInfo: React.FC = () => {
  return (
    <GuidePageLayout
      title="全ユーザーにお知らせを配信する"
      description="全ユーザーに対してお知らせ（INFO）または重要なお知らせ（IMPORTANT）を配信します。"
      breadcrumbs={[{ label: '通知管理', path: '/guide/notice/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '管理者メニューから通知管理画面（お知らせタブ or 重要なお知らせタブ）を開く' },
            { label: '「お知らせを新規作成」ボタンを押下する' },
            {
              label: '通知内容を入力する（対象ユーザーの指定は不要）',
              description: 'タイトル左の戻る矢印をクリックすると、作成を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="お知らせ作成フォーム">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        お知らせの新規作成
                      </Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <GuideMockTextField label="タイトル" required />
                      <TextField
                        label="内容"
                        required
                        fullWidth
                        size="small"
                        multiline
                        rows={4}
                        sx={{
                          pointerEvents: 'none',
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        }}
                      />
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <GuideMockTextField label="期限" />
                        </Grid>
                        <Grid size={6}>
                          <GuideMockToggleButtonGroup
                            label="優先度（1: 最高 〜 5: 最低）"
                            options={[1, 2, 3, 4, 5]}
                            value={5}
                          />
                        </Grid>
                      </Grid>
                      <GuideMockTextField label="リンク" placeholder="https://..." />
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「作成」ボタンを押下する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="作成" startIcon={<NoteAddIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '作成を破棄して一覧画面に戻る'],
            ['作成ボタン', '入力内容でお知らせを作成する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            INFO/IMPORTANT を選択した場合、対象ユーザーの入力は不要です（全ユーザーに自動配信）。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            登録後に追加された新規ユーザーにはこの通知は届きません。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideNoticeCreateInfo;
